import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CompanyView, QuickSearchCompaniesParameters } from '@api-proxies';
import { CompanyService } from '@http/clients/company.service';
import { FilterStateService } from '@services/filter-state.service';
import { AdditionalFiltersComponent } from '@shared/additional-filters/additional-filters.component';
import { ClearFiltersButtonComponent } from '@shared/clear-filters-button/clear-filters-button.component';
import { ITypeAheadService } from '@shared/generic-type-ahead/generic-type-ahead.component';
import { FilterService, IGetById, SearchInputComponent } from 'ngx-lib';
import { Subscription } from 'rxjs';
import { CONTACTS_FILTER_CONFIG, CONTACTS_FILTER_KEY, IFilterValueContacts } from './contacts-filter.config';

@Component({
    selector: 'app-contacts-filters',
    imports: [SearchInputComponent, AdditionalFiltersComponent, ClearFiltersButtonComponent],
    templateUrl: './contacts-filters.component.html',
    styleUrl: './contacts-filters.component.scss'
})
export class ContactsFiltersComponent implements OnInit, OnDestroy {
    private readonly filterService = inject(FilterService);
    private readonly filterStateService = inject(FilterStateService<IFilterValueContacts>);
    private readonly companyService = inject(CompanyService);
    private readonly activatedRoute = inject(ActivatedRoute);

    filters: IFilterValueContacts = {};

    companyServiceFilter?: ITypeAheadService<CompanyView, QuickSearchCompaniesParameters>;

    disabledClearFiltersBtn = false;

    filtersKey = CONTACTS_FILTER_KEY;

    additionalFiltersConfig = CONTACTS_FILTER_CONFIG;

    private filterSubscription?: Subscription;
    async ngOnInit(): Promise<void> {
        this.companyServiceFilter = this.companyService;

        await this.deserializeUrlParams();

        if (this.filtersKey && Object.keys(this.activatedRoute.snapshot.queryParams).length === 0) {
            const filtersFromLocalStorage = this.filterStateService.getFromLocalStorage(this.filtersKey);
            this.filters = {
                ...this.filters,
                ...(filtersFromLocalStorage || {})
            };
        }

        this.filterStateService.updateFilters(this.filters);

        this.filterSubscription = this.filterStateService.stateFilters$.subscribe(async (filters) => {
            if (!filters) {
                this.filters = {} as IFilterValueContacts;
            } else {
                this.filters = filters as IFilterValueContacts;
                await this.updateFilterUrlParams();
                this.disabledClearFiltersBtn = !this.someFilterApplied();
            }
            this.filterStateService.saveToLocalStorage(this.filtersKey, this.filters);
        });
    }

    ngOnDestroy(): void {
        if (this.filterSubscription) {
            this.filterSubscription.unsubscribe();
        }
    }

    onSearchTextChanged(searchText: string): void {
        this.filters.searchText = searchText;
        this.filterStateService.updateFilters(this.filters);
    }

    private async updateFilterUrlParams(): Promise<void> {
        await this.filterService.serialize<IFilterValueContacts>(this.filters, 'sortBy');
        await this.filterService.serialize<IFilterValueContacts>(this.filters, 'sortDirection');
        await this.filterService.serialize<IFilterValueContacts>(this.filters, 'searchText');
        await this.filterService.serializeTypeAhead<IFilterValueContacts, CompanyView>(
            'company',
            this.filters.company,
            'id'
        );
        await this.filterService.serializeBoolean<IFilterValueContacts>(this.filters, 'includeArchived');
    }

    private async deserializeUrlParams() {
        this.filterService.deserialize<IFilterValueContacts>(this.filters, 'sortBy');
        this.filterService.deserialize<IFilterValueContacts>(this.filters, 'sortDirection');
        this.filterService.deserialize<IFilterValueContacts>(this.filters, 'searchText');
        await this.filterService.deserializeTypeAhead<IFilterValueContacts, CompanyView>(
            this.filters,
            'company',
            this.companyServiceFilter as IGetById
        );
        this.filterService.deserializeBoolean<IFilterValueContacts>(this.filters, 'includeArchived');
    }

    private someFilterApplied(): boolean {
        return Object.keys(this.filters).length > 0;
    }
}
